import React from 'react';
import { usePackagingOperationsContext } from '../context/PackagingOperationsContext';

const SummaryComponent: React.FC = () => {
    const { state } = usePackagingOperationsContext();

    const isSealIntegrity = state.sessionData.pkgopsType === 'Seal Integrity';
    const sealData = state.checklistData;
    const machines = isSealIntegrity && sealData?.machines ? sealData.machines : [];

    return (
        <div className="bg-white rounded-lg shadow-sm border border-slate-200">
            <div className="bg-slate-100 px-4 py-3 border-b border-slate-200 rounded-t-lg flex justify-between items-center">
                <h2 className="text-lg font-semibold text-slate-800">Audit Session Summary</h2>
                <span className="bg-green-100 text-green-800 text-xs font-semibold px-2.5 py-0.5 rounded border border-green-200">
                    Completed & Closed
                </span>
            </div>

            <div className="p-5 space-y-6">
                <div className="text-center py-4 border-b border-slate-100">
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-green-100 text-green-600 mb-2 text-2xl">
                        ✓
                    </div>
                    <h3 className="text-xl font-bold text-slate-800">Quality Inspection Complete</h3>
                    <p className="text-sm text-gray-500 mt-1">
                        The inspection for <strong>{state.sessionData.pkgopsType}</strong> on <strong>{state.sessionData.lineNo}</strong> has been successfully recorded and closed.
                    </p>
                </div>

                {/* Seal Integrity Specific Breakdown */}
                {isSealIntegrity && machines.length > 0 && (
                    <div className="space-y-4">
                        <div className="flex justify-between items-center">
                            <h4 className="text-sm font-bold uppercase tracking-wider text-slate-700">
                                Machine Inspection Breakdown ({machines.length} Machines)
                            </h4>
                            <span className="text-xs text-slate-500 font-medium">
                                Product: <strong>{sealData?.productName}</strong> ({sealData?.sku || 'N/A'})
                            </span>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {machines.map((m: any, idx: number) => {
                                const hasLeaks = (m.totalLeaks || 0) > 0;
                                return (
                                    <div key={idx} className="border rounded-lg p-4 bg-slate-50 border-slate-200 space-y-2">
                                        <div className="flex justify-between items-center border-b border-slate-200 pb-2">
                                            <span className="font-bold text-slate-800">{m.machineNo}</span>
                                            <span className={`text-xs font-bold px-2 py-0.5 rounded ${hasLeaks ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                                                {m.passPctStr || (m.passPct + '%')} PASS
                                            </span>
                                        </div>

                                        <div className="grid grid-cols-3 gap-2 text-center text-xs py-1">
                                            <div className="bg-white p-1.5 rounded border border-slate-100">
                                                <span className="text-slate-400 block">Tested</span>
                                                <span className="font-bold text-slate-800">{m.sampleQty}</span>
                                            </div>
                                            <div className="bg-white p-1.5 rounded border border-slate-100">
                                                <span className="text-slate-400 block">Passed</span>
                                                <span className="font-bold text-green-600">{m.passedCount}</span>
                                            </div>
                                            <div className="bg-white p-1.5 rounded border border-slate-100">
                                                <span className="text-slate-400 block">Leaks</span>
                                                <span className={`font-bold ${hasLeaks ? 'text-red-600' : 'text-slate-600'}`}>{m.totalLeaks}</span>
                                            </div>
                                        </div>

                                        {hasLeaks && m.defects && m.defects.length > 0 && (
                                            <div className="text-xs space-y-1 pt-1 border-t border-slate-200">
                                                <span className="font-semibold text-slate-600 block">Defects:</span>
                                                {m.defects.map((d: any, dIdx: number) => (
                                                    <div key={dIdx} className="flex justify-between text-slate-600">
                                                        <span>• {d.type} {d.custom ? `(${d.custom})` : ''}</span>
                                                        <span className="font-bold text-red-600">{d.count} pk</span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* PAPA Specific Breakdown */}
                {state.sessionData.pkgopsType === 'PAPA' && state.checklistData && (
                    <div className="space-y-6">
                        {/* Header Details */}
                        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 flex flex-wrap justify-between items-center gap-3">
                            <div>
                                <span className="text-xs uppercase font-bold text-slate-500 tracking-wider block">PAPA Inspected Variety</span>
                                <span className="text-base font-bold text-slate-800">
                                    {state.checklistData.metadata?.productName || 'N/A'}
                                </span>
                                {state.checklistData.metadata?.sku && (
                                    <span className="ml-2 text-xs font-semibold px-2 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200">
                                        SKU: {state.checklistData.metadata?.sku}
                                    </span>
                                )}
                            </div>
                            <div className="flex flex-wrap gap-4 text-xs text-slate-600">
                                <div>
                                    <span className="text-slate-400 block font-medium">Sample Size:</span>
                                    <span className="font-bold text-slate-800">
                                        {state.checklistData.metadata?.sampleSize || 100} biscuits
                                    </span>
                                </div>
                                <div>
                                    <span className="text-slate-400 block font-medium">Batch No:</span>
                                    <span className="font-bold text-slate-800">
                                        {state.checklistData.metadata?.batchNo || 'N/A'}
                                    </span>
                                </div>
                                <div>
                                    <span className="text-slate-400 block font-medium">Overall Rate:</span>
                                    <span className={`font-bold font-mono ${
                                        (state.checklistData.stats?.isCriticalDefectPresent || state.checklistData.totalCount > 0)
                                            ? 'text-red-600'
                                            : 'text-emerald-600'
                                    }`}>
                                        {state.checklistData.totalPct || '0.00'}%
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Defect KPI Summary Cards */}
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            <div className={`p-3.5 rounded-lg border text-center ${
                                (state.checklistData.stats?.catACount || 0) > 0
                                    ? 'bg-red-50 border-red-200 text-red-900'
                                    : 'bg-slate-50 border-slate-200 text-slate-800'
                            }`}>
                                <span className="text-[11px] font-bold uppercase tracking-wider block text-red-600">
                                    Category A (Critical)
                                </span>
                                <span className="text-xl font-black font-mono block mt-0.5">
                                    {state.checklistData.stats?.catACount || 0} ({state.checklistData.stats?.catAPct?.toFixed(2) || '0.00'}%)
                                </span>
                                <span className="text-[10px] text-slate-500 block">Zero Tolerance Target</span>
                            </div>

                            <div className={`p-3.5 rounded-lg border text-center ${
                                (state.checklistData.stats?.catBCount || 0) > 0
                                    ? 'bg-amber-50 border-amber-200 text-amber-900'
                                    : 'bg-slate-50 border-slate-200 text-slate-800'
                            }`}>
                                <span className="text-[11px] font-bold uppercase tracking-wider block text-amber-700">
                                    Category B (Major)
                                </span>
                                <span className="text-xl font-black font-mono block mt-0.5">
                                    {state.checklistData.stats?.catBCount || 0} ({state.checklistData.stats?.catBPct?.toFixed(2) || '0.00'}%)
                                </span>
                                <span className="text-[10px] text-slate-500 block">Functional & Appearance</span>
                            </div>

                            <div className={`p-3.5 rounded-lg border text-center ${
                                (state.checklistData.stats?.catCCount || 0) > 0
                                    ? 'bg-blue-50 border-blue-200 text-blue-900'
                                    : 'bg-slate-50 border-slate-200 text-slate-800'
                            }`}>
                                <span className="text-[11px] font-bold uppercase tracking-wider block text-blue-700">
                                    Category C (Minor)
                                </span>
                                <span className="text-xl font-black font-mono block mt-0.5">
                                    {state.checklistData.stats?.catCCount || 0} ({state.checklistData.stats?.catCPct?.toFixed(2) || '0.00'}%)
                                </span>
                                <span className="text-[10px] text-slate-500 block">Aesthetic Variations</span>
                            </div>
                        </div>

                        {/* Observed Defects List */}
                        {(() => {
                            const observed = (state.checklistData.activeDefects && state.checklistData.activeDefects.length > 0)
                                ? state.checklistData.activeDefects
                                : (state.checklistData.defects || []).filter((d: any) => d.selected && d.count > 0);

                            if (observed.length === 0) {
                                return (
                                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-center">
                                        <span className="text-emerald-700 font-bold text-sm block">
                                            ✓ 100% Quality Conformance
                                        </span>
                                        <p className="text-xs text-emerald-600 mt-1 m-0">
                                            Zero defects observed across all {state.checklistData.metadata?.sampleSize || 100} biscuits tested during this PAPA audit.
                                        </p>
                                    </div>
                                );
                            }

                            return (
                                <div className="space-y-3">
                                    <div className="flex justify-between items-center">
                                        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700 m-0">
                                            Observed Defects Breakdown ({observed.length} Defect Types)
                                        </h4>
                                        <span className="text-xs font-bold text-slate-500">
                                            Total Defect Pieces: {state.checklistData.totalCount || 0}
                                        </span>
                                    </div>

                                    <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
                                        <table className="min-w-full divide-y divide-slate-200 text-xs">
                                            <thead className="bg-slate-50 font-bold text-slate-600">
                                                <tr>
                                                    <th className="py-2.5 px-3 text-left">Category & Type</th>
                                                    <th className="py-2.5 px-3 text-left">Defect Name & Notes</th>
                                                    <th className="py-2.5 px-3 text-center">Count</th>
                                                    <th className="py-2.5 px-3 text-right">Defect %</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-slate-100">
                                                {observed.map((d: any, idx: number) => {
                                                    const isCatA = d.category === 'A';
                                                    const isCatB = d.category === 'B';
                                                    const catBadge = isCatA
                                                        ? 'bg-red-100 text-red-800 border-red-200'
                                                        : isCatB
                                                        ? 'bg-amber-100 text-amber-800 border-amber-200'
                                                        : 'bg-blue-100 text-blue-800 border-blue-200';

                                                    return (
                                                        <tr key={idx} className="hover:bg-slate-50/60">
                                                            <td className="py-2 px-3 whitespace-nowrap">
                                                                <span className={`px-2 py-0.5 rounded text-[10px] font-bold border ${catBadge}`}>
                                                                    Cat {d.category} • {d.type}
                                                                </span>
                                                            </td>
                                                            <td className="py-2 px-3">
                                                                <span className="font-bold text-slate-800 block">{d.name}</span>
                                                                {d.remarks && (
                                                                    <span className="text-[11px] text-indigo-700 bg-indigo-50 px-1.5 py-0.2 rounded border border-indigo-100 inline-block mt-0.5">
                                                                        Remarks: {d.remarks}
                                                                    </span>
                                                                )}
                                                            </td>
                                                            <td className="py-2 px-3 text-center font-mono font-bold text-slate-800">
                                                                {d.count} pcs
                                                            </td>
                                                            <td className="py-2 px-3 text-right font-mono font-bold text-slate-700">
                                                                {typeof d.pct === 'number' ? `${d.pct.toFixed(2)}%` : d.pct}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            );
                        })()}
                    </div>
                )}

                {/* PQI Specific Breakdown */}
                {state.sessionData.pkgopsType === 'PQI' && state.checklistData && (
                    <div className="space-y-6">
                        {/* Header Details */}
                        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 flex flex-wrap justify-between items-center gap-3">
                            <div>
                                <span className="text-xs uppercase font-bold text-slate-500 tracking-wider block">Product Variety</span>
                                <span className="text-base font-bold text-slate-800">{state.checklistData.productName || 'N/A'}</span>
                                {state.checklistData.sku && (
                                    <span className="ml-2 text-xs font-semibold px-2 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200">
                                        SKU: {state.checklistData.sku}
                                    </span>
                                )}
                            </div>
                            <div className="flex gap-4 text-xs text-slate-600">
                                {state.checklistData.pkd && (
                                    <div>
                                        <span className="text-slate-400 block font-medium">PKD:</span>
                                        <span className="font-bold">{state.checklistData.pkd}</span>
                                    </div>
                                )}
                                {state.checklistData.batchCode && (
                                    <div>
                                        <span className="text-slate-400 block font-medium">Batch:</span>
                                        <span className="font-bold">{state.checklistData.batchCode}</span>
                                    </div>
                                )}
                                <div>
                                    <span className="text-slate-400 block font-medium">Standard Wt:</span>
                                    <span className="font-bold text-slate-800">{state.checklistData.standardWeight || 150}g</span>
                                </div>
                            </div>
                        </div>

                        {/* Net Weight Summary Card */}
                        <div className="border border-slate-200 rounded-lg p-4 bg-white shadow-sm space-y-4">
                            <div className="flex justify-between items-center border-b border-slate-100 pb-3">
                                <div>
                                    <h4 className="text-sm font-bold uppercase tracking-wider text-slate-800 flex items-center gap-2">
                                        <span>⚖</span> Net Weight Evaluation
                                    </h4>
                                    <p className="text-xs text-slate-500 mt-0.5">15 individual pack weights measured against target standard</p>
                                </div>
                                <span className={`text-xs font-bold px-2.5 py-1 rounded border ${
                                    state.checklistData.isUnderweight
                                        ? 'bg-red-50 text-red-700 border-red-200'
                                        : 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                }`}>
                                    {state.checklistData.isUnderweight ? '⚠ Shortfall (Underweight)' : '✓ In-Spec'}
                                </span>
                            </div>

                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                <div className="bg-slate-50 p-3 rounded-lg border border-slate-100 text-center">
                                    <span className="text-xs text-slate-500 block font-medium">Standard Weight</span>
                                    <span className="text-base font-bold text-slate-800">{state.checklistData.standardWeight || 150}g</span>
                                </div>
                                <div className={`p-3 rounded-lg border text-center ${
                                    state.checklistData.isUnderweight
                                        ? 'bg-red-50 border-red-200 text-red-800'
                                        : 'bg-slate-50 border-slate-100 text-slate-800'
                                }`}>
                                    <span className="text-xs text-slate-500 block font-medium">Average Weight</span>
                                    <span className="text-base font-bold">
                                        {state.checklistData.avgWeight || `${(state.checklistData.averageWeight || 0).toFixed(2)}g`}
                                    </span>
                                </div>
                                <div className="bg-emerald-50 p-3 rounded-lg border border-emerald-100 text-center">
                                    <span className="text-xs text-emerald-700 block font-medium">Give-Away (Overweight)</span>
                                    <span className="text-base font-bold text-emerald-800">
                                        +{state.checklistData.giveAwayStr || `${(state.checklistData.giveAway || 0).toFixed(2)}g`}
                                    </span>
                                </div>
                                <div className={`p-3 rounded-lg border text-center ${
                                    (state.checklistData.shortfall || 0) > 0
                                        ? 'bg-red-50 border-red-200 text-red-800'
                                        : 'bg-slate-50 border-slate-100 text-slate-400'
                                }`}>
                                    <span className="text-xs block font-medium">Underweight Shortfall</span>
                                    <span className="text-base font-bold">
                                        {(state.checklistData.shortfall || 0) > 0 ? `-${(state.checklistData.shortfall || 0).toFixed(2)}g` : '0.00g'}
                                    </span>
                                </div>
                            </div>

                            {/* 15 Samples Chips */}
                            {state.checklistData.nwSamples && state.checklistData.nwSamples.length > 0 && (
                                <div className="pt-2 border-t border-slate-100">
                                    <span className="text-xs font-semibold text-slate-500 block mb-2">Recorded Sample Weights (15 Pkts):</span>
                                    <div className="grid grid-cols-5 sm:grid-cols-8 md:grid-cols-15 gap-1.5 text-xs text-center">
                                        {state.checklistData.nwSamples.map((w: any, idx: number) => {
                                            const num = parseFloat(w);
                                            const std = state.checklistData.standardWeight || 150;
                                            const isLow = !isNaN(num) && num > 0 && num < std;
                                            return (
                                                <div
                                                    key={idx}
                                                    className={`py-1 px-1 rounded border text-xs font-mono font-medium ${
                                                        isLow
                                                            ? 'bg-red-50 text-red-700 border-red-200'
                                                            : !isNaN(num) && num > 0
                                                            ? 'bg-slate-50 text-slate-700 border-slate-200'
                                                            : 'bg-gray-50 text-gray-400 border-gray-100'
                                                    }`}
                                                    title={`Sample ${idx + 1}: ${w || '-'}g`}
                                                >
                                                    <span className="block text-[10px] text-slate-400 font-sans">#{idx + 1}</span>
                                                    {w ? `${w}g` : '-'}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Evaluations Breakdown: Product, Primary, Secondary, CBB */}
                        <div>
                            <h4 className="text-sm font-bold uppercase tracking-wider text-slate-700 mb-3">
                                Visual & Defect Pack Evaluations (10 Samples Each)
                            </h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                {[
                                    { title: 'Product Evaluation', samples: state.checklistData.productSamples },
                                    { title: 'Primary Packaging', samples: state.checklistData.primarySamples },
                                    { title: 'Secondary Packaging', samples: state.checklistData.secondarySamples },
                                    { title: 'CBB Corrugated Box', samples: state.checklistData.cbbSamples }
                                ].map((group, gIdx) => {
                                    const sampleList = group.samples || [];
                                    const defective = sampleList.filter((s: any) => s.status === 'Not Okay');
                                    const passed = sampleList.filter((s: any) => s.status === 'Okay').length;
                                    const hasDefects = defective.length > 0;

                                    return (
                                        <div key={gIdx} className="border border-slate-200 rounded-lg p-3.5 bg-slate-50 space-y-2">
                                            <div className="flex justify-between items-center border-b border-slate-200 pb-2">
                                                <span className="font-bold text-xs text-slate-800">{group.title}</span>
                                                <span className={`text-[11px] font-bold px-2 py-0.5 rounded ${
                                                    hasDefects ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'
                                                }`}>
                                                    {hasDefects ? `${defective.length} DEFECTS` : 'ALL OK'}
                                                </span>
                                            </div>

                                            <div className="grid grid-cols-2 gap-2 text-center text-xs py-1">
                                                <div className="bg-white p-1.5 rounded border border-slate-100">
                                                    <span className="text-slate-400 block text-[10px]">Passed</span>
                                                    <span className="font-bold text-emerald-600">{passed} / 10</span>
                                                </div>
                                                <div className="bg-white p-1.5 rounded border border-slate-100">
                                                    <span className="text-slate-400 block text-[10px]">Defective</span>
                                                    <span className={`font-bold ${hasDefects ? 'text-red-600' : 'text-slate-600'}`}>
                                                        {defective.length}
                                                    </span>
                                                </div>
                                            </div>

                                            {hasDefects && (
                                                <div className="text-[11px] space-y-1 pt-1 border-t border-slate-200">
                                                    <span className="font-semibold text-slate-600 block">Recorded Defects:</span>
                                                    {defective.map((d: any, dIdx: number) => (
                                                        <div key={dIdx} className="text-red-600 flex justify-between">
                                                            <span>• S{d.id + 1}: {d.category}</span>
                                                            {d.detail && <span className="text-slate-500 truncate max-w-[90px]">{d.detail}</span>}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}

                {/* Cream Percentage Specific Breakdown */}
                {state.sessionData.pkgopsType === 'Cream Percentage' && state.checklistData && (
                    <div className="space-y-6">
                        {/* Header Details & Standards */}
                        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 flex flex-wrap justify-between items-center gap-3">
                            <div>
                                <span className="text-xs uppercase font-bold text-slate-500 tracking-wider block">Cream Variety</span>
                                <span className="text-base font-bold text-slate-800">
                                    {state.checklistData.metadata?.productName || 'N/A'}
                                </span>
                                {state.checklistData.metadata?.productCode && (
                                    <span className="ml-2 text-xs font-semibold px-2 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200">
                                        SKU: {state.checklistData.metadata?.productCode}
                                    </span>
                                )}
                            </div>
                            <div className="flex flex-wrap gap-4 text-xs text-slate-600">
                                <div>
                                    <span className="text-slate-400 block font-medium">Standard Target:</span>
                                    <span className="font-bold text-indigo-700">
                                        {state.checklistData.metadata?.standardCreamPercentage || '28.00'}%
                                    </span>
                                </div>
                                <div>
                                    <span className="text-slate-400 block font-medium">Tolerance Range:</span>
                                    <span className="font-bold text-slate-800">
                                        {state.checklistData.metadata?.standardMin || '26.00'}% – {state.checklistData.metadata?.standardMax || '30.00'}%
                                    </span>
                                </div>
                                <div>
                                    <span className="text-slate-400 block font-medium">QA Standard:</span>
                                    <span className="font-bold text-slate-700 truncate max-w-[180px] block">
                                        {state.checklistData.metadata?.ruleDescription || 'Cremica QA Specification'}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Machine-Wise Evaluation Breakdown */}
                        <div>
                            <div className="flex justify-between items-center mb-3">
                                <h4 className="text-sm font-bold uppercase tracking-wider text-slate-700">
                                    Machine-Wise Cream % Breakdown ({
                                        (state.checklistData.machines && state.checklistData.machines.length) || 1
                                    } Machines)
                                </h4>
                                <span className="text-xs text-slate-500">
                                    Formula: ((Wt. Sandwich − Wt. Shell) / Wt. Sandwich) × 100
                                </span>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {(state.checklistData.machines || [{
                                    machineNo: state.checklistData.metadata?.machineNo || 'M/C-01',
                                    sandwichWeights: state.checklistData.weightData?.sandwichWeights || ['', '', '', ''],
                                    shellWeights: state.checklistData.weightData?.shellWeights || ['', '', '', ''],
                                    samplePercentages: ['0.00', '0.00', '0.00', '0.00'],
                                    averageCreamPercentage: state.checklistData.averageCreamPercentage || '0.00',
                                    status: state.checklistData.status || { text: 'Pass' }
                                }]).map((m: any, idx: number) => {
                                    const isFail = m.status?.text === 'Fail';
                                    const min = parseFloat(state.checklistData.metadata?.standardMin) || 0;
                                    const max = parseFloat(state.checklistData.metadata?.standardMax) || 100;

                                    return (
                                        <div key={idx} className="border border-slate-200 rounded-lg p-4 bg-slate-50 space-y-3">
                                            <div className="flex justify-between items-center border-b border-slate-200 pb-2">
                                                <span className="font-bold text-slate-800">{m.machineNo}</span>
                                                <span className={`text-xs font-bold px-2 py-0.5 rounded ${
                                                    isFail ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'
                                                }`}>
                                                    {isFail ? 'FAIL (Out of Spec)' : 'PASS'}
                                                </span>
                                            </div>

                                            {/* Machine Average Callout */}
                                            <div className="bg-white p-3 rounded-lg border border-slate-100 text-center">
                                                <span className="text-xs text-slate-400 block uppercase font-bold tracking-wider">
                                                    Average Cream %
                                                </span>
                                                <span className="text-2xl font-black font-mono text-slate-800 block">
                                                    {m.averageCreamPercentage}%
                                                </span>
                                                <span className="text-[11px] text-slate-500">
                                                    Spec: {min}% – {max}%
                                                </span>
                                            </div>

                                            {/* 4 Samples Grid */}
                                            <div className="space-y-1.5 pt-1 text-xs">
                                                <span className="font-semibold text-slate-600 block text-[11px]">
                                                    Sample Measurements (Sandwich / Shell / Cream %):
                                                </span>
                                                {(m.sandwichWeights || []).map((sw: string, sIdx: number) => {
                                                    const shw = (m.shellWeights || [])[sIdx] || '-';
                                                    const swNum = parseFloat(sw) || 0;
                                                    const shNum = parseFloat(shw) || 0;
                                                    const pct = swNum > 0 && swNum >= shNum ? (((swNum - shNum) / swNum) * 100).toFixed(2) : '-';
                                                    const pctNum = parseFloat(pct) || 0;
                                                    const isOutOfSpec = pct !== '-' && (pctNum < min || pctNum > max);

                                                    return (
                                                        <div
                                                            key={sIdx}
                                                            className="flex justify-between items-center bg-white p-1.5 rounded border border-slate-100"
                                                        >
                                                            <span className="font-medium text-slate-500">S{sIdx + 1}</span>
                                                            <span className="font-mono text-slate-600">{sw ? `${sw}g` : '-'}</span>
                                                            <span className="text-slate-400">/</span>
                                                            <span className="font-mono text-slate-600">{shw ? `${shw}g` : '-'}</span>
                                                            <span className={`font-mono font-bold ${
                                                                isOutOfSpec ? 'text-red-600' : 'text-indigo-600'
                                                            }`}>
                                                                {pct !== '-' ? `${pct}%` : '-'}
                                                            </span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}

                {/* Quality Wall Records Specific Breakdown */}
                {state.sessionData.pkgopsType === 'Quality Wall Records' && state.checklistData && (
                    <div className="space-y-6">
                        {/* Header Details */}
                        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 flex flex-wrap justify-between items-center gap-3">
                            <div>
                                <span className="text-xs uppercase font-bold text-slate-500 tracking-wider block">Quality Wall Evaluation</span>
                                <span className="text-base font-bold text-slate-800">
                                    {state.checklistData.qualityWall?.productName || state.checklistData.metadata?.productName || 'N/A'}
                                </span>
                                {(state.checklistData.qualityWall?.sku || state.checklistData.metadata?.sku) && (
                                    <span className="ml-2 text-xs font-semibold px-2 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200">
                                        SKU: {state.checklistData.qualityWall?.sku || state.checklistData.metadata?.sku}
                                    </span>
                                )}
                            </div>
                            <div className="flex flex-wrap gap-4 text-xs text-slate-600">
                                <div>
                                    <span className="text-slate-400 block font-medium">Batch No:</span>
                                    <span className="font-bold text-slate-800">
                                        {state.checklistData.qualityWall?.batchNo || state.checklistData.metadata?.batchNo || 'N/A'}
                                    </span>
                                </div>
                                <div>
                                    <span className="text-slate-400 block font-medium">Wall Type:</span>
                                    <span className="font-bold text-slate-800">
                                        {state.checklistData.qualityWall?.wallType || state.checklistData.metadata?.wallType || 'Routine Wall'}
                                    </span>
                                </div>
                                <div>
                                    <span className="text-slate-400 block font-medium">Facilitator:</span>
                                    <span className="font-bold text-indigo-700">
                                        {state.checklistData.qualityWall?.facilitator || state.checklistData.metadata?.facilitator || 'QA Lead'}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Overall Verdict Card */}
                        {(() => {
                            const qw = state.checklistData.qualityWall;
                            const isAbort = qw?.overallAction === 'Abort' || qw?.overallStatus === 'Hot Wall';
                            const isAction = qw?.overallAction === 'Action Taken' || qw?.overallStatus === 'Yellow Wall';

                            const bannerBg = isAbort
                                ? 'bg-red-50 border-red-200'
                                : isAction
                                ? 'bg-amber-50 border-amber-200'
                                : 'bg-emerald-50 border-emerald-200';

                            const badgeBg = isAbort
                                ? 'bg-red-600 text-white'
                                : isAction
                                ? 'bg-amber-500 text-white'
                                : 'bg-emerald-600 text-white';

                            return (
                                <div className={`border rounded-xl p-5 ${bannerBg} space-y-3`}>
                                    <div className="flex flex-wrap justify-between items-center gap-3">
                                        <div>
                                            <span className="text-xs uppercase font-bold tracking-wider text-slate-500 block">
                                                Overall Quality Wall Verdict
                                            </span>
                                            <h4 className="text-xl font-black text-slate-800 m-0 flex items-center gap-2">
                                                <span>{isAbort ? '🛑' : isAction ? '🟡' : '🟢'}</span>
                                                <span>{qw?.overallStatus || 'Green Wall'}</span>
                                                <span className={`text-xs px-2.5 py-0.5 rounded-full font-bold ${badgeBg}`}>
                                                    ACTION: {qw?.overallAction || 'Sustain'}
                                                </span>
                                            </h4>
                                        </div>

                                        <div className="flex items-center gap-4 text-center">
                                            <div className="bg-white/80 backdrop-blur px-3 py-1.5 rounded-lg border border-slate-200">
                                                <span className="text-[10px] text-slate-400 uppercase font-bold block">Peak Score</span>
                                                <span className="text-lg font-black font-mono text-slate-800">{qw?.overallScore ?? 0} / 4</span>
                                            </div>
                                            <div className="bg-white/80 backdrop-blur px-3 py-1.5 rounded-lg border border-slate-200">
                                                <span className="text-[10px] text-slate-400 uppercase font-bold block">Avg Score</span>
                                                <span className="text-lg font-black font-mono text-slate-800">
                                                    {(qw?.averageScore || 0).toFixed(2)} / 4
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    {qw?.remarks && (
                                        <p className="text-xs text-slate-600 bg-white/70 p-2.5 rounded-lg border border-slate-200/60 m-0">
                                            <strong>Remarks:</strong> {qw.remarks}
                                        </p>
                                    )}
                                </div>
                            );
                        })()}

                        {/* Individual Team Member Scores Grid */}
                        <div>
                            <div className="flex justify-between items-center mb-3">
                                <h4 className="text-sm font-bold uppercase tracking-wider text-slate-700">
                                    Individual Team Member Evaluations ({
                                        (state.checklistData.qualityWall?.members && state.checklistData.qualityWall.members.length) || 0
                                    } Members)
                                </h4>
                                <span className="text-xs text-slate-500">
                                    Scoring Scale: 0-1 (Green/Sustain) • 2-3 (Yellow/Action) • 4 (Hot/Abort)
                                </span>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                {(state.checklistData.qualityWall?.members || []).map((m: any, idx: number) => {
                                    const isAbort = m.score >= 4;
                                    const isAction = m.score >= 2 && m.score <= 3;

                                    return (
                                        <div key={idx} className="border border-slate-200 rounded-lg p-3.5 bg-slate-50 space-y-2">
                                            <div className="flex justify-between items-center border-b border-slate-200 pb-2">
                                                <div>
                                                    <span className="font-bold text-xs text-slate-800 block">{m.name}</span>
                                                    <span className="text-[11px] text-slate-400">{m.role || 'Reviewer'}</span>
                                                </div>
                                                <span className={`text-[11px] font-bold px-2 py-0.5 rounded ${
                                                    isAbort
                                                        ? 'bg-red-100 text-red-800 border border-red-200'
                                                        : isAction
                                                        ? 'bg-amber-100 text-amber-800 border border-amber-200'
                                                        : 'bg-emerald-100 text-emerald-800 border border-emerald-200'
                                                }`}>
                                                    Score {m.score}: {m.status}
                                                </span>
                                            </div>

                                            <div className="flex justify-between items-center text-xs">
                                                <span className="text-slate-500">Action:</span>
                                                <span className="font-bold text-slate-700">{m.action}</span>
                                            </div>

                                            {m.remarks && (
                                                <p className="text-[11px] text-slate-600 bg-white p-2 rounded border border-slate-100 m-0">
                                                    {m.remarks}
                                                </p>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SummaryComponent;

